import { getInstallLink } from '../services/apis';
import { installFromShareUrl } from '@wix/ambassador-devcenter-site-apps-v1-app/http';
import { reportInstallFailed, reportInstallRequestFired, reportInstallSuccess } from '../bi/bi';
import { useAdvanceOauth } from './useAdvanceOauth';
import { getVersionToInstall } from '../selectors/appForInstallation';
import { INSTALLER_ERRORS } from '../errors';
import { getIsAdvanceOauthApp } from '../selectors/appRuntimeReader';
import { wait } from '../utils/general';
import isEmpty from 'lodash/isEmpty';
import { convertResponseToProvisionSuccess } from '../utils/provision';
import { useHandleError } from '../components/ErrorBoundary/ErrorHandlerProvider';
import { usePanorama } from '../context/PanoramaContext';
import { useInstallerContext } from '../context/InstallerContext';
import { setIsBulkInstallTriggered } from '../selectors/bulk-installer-triggered';
import { useRenderControl } from '../context/InstallerRenderControlContext';
export const useProvisionApp = () => {
  const {
    appId,
    tenantId,
    httpClient,
    version,
    setIsFinishLoading,
    preInstallationData,
    onProvisionSuccess,
    versionOverrideId,
    shareUrlId
  } = useInstallerContext();
  const {
    experiments
  } = useRenderControl();
  const {
    startTransaction,
    finishTransaction,
    panoramaLogger
  } = usePanorama();
  const {
    appForInstallation,
    authorizationHeader,
    app
  } = preInstallationData;
  const isAdvanceOauthApp = getIsAdvanceOauthApp(app);
  const {
    handleError
  } = useHandleError();
  const getOauth2 = useAdvanceOauth();
  const isNewApiForShareUrlEnabled = shareUrlId && (experiments == null ? void 0 : experiments.enabled('spec.dcc.enableNewApiForShareUrl'));
  const shouldAddOverrideId = version === 'dev' && versionOverrideId;
  return async () => {
    reportInstallRequestFired();
    const oauth2 = isAdvanceOauthApp ? await getOauth2() : {};
    if (isAdvanceOauthApp && !oauth2) {
      return; // handle error in useAdvanceOauth hook - don't continue the flow
    }
    try {
      var _installationResponse;
      setIsBulkInstallTriggered(true);
      const transactionPayload = {
        isAdvanceOauthApp,
        version,
        ...(shareUrlId && {
          shareUrlId
        }),
        ...(versionOverrideId && {
          versionOverrideId
        })
      };
      let installationResponse;
      let requestId;
      startTransaction(isNewApiForShareUrlEnabled ? 'install-from-share-url' : 'bulk-install', transactionPayload);
      if (isNewApiForShareUrlEnabled) {
        const {
          data,
          requestId: _requestId
        } = await httpClient.request(installFromShareUrl({
          shareUrlId,
          ...(shouldAddOverrideId && {
            devVersion: {
              overrideId: versionOverrideId
            }
          })
        }));
        installationResponse = data;
        requestId = _requestId;
      } else {
        const {
          data,
          requestId: _requestId
        } = await httpClient.post(getInstallLink(), {
          apps: [{
            id: appId,
            version: getVersionToInstall(appForInstallation, version),
            ...oauth2,
            ...(shouldAddOverrideId && {
              // todo sled test when create dev sites is fixed
              devVersion: {
                overrideId: versionOverrideId
              }
            })
          }]
        }, {
          headers: {
            authorization: authorizationHeader
          }
        });
        installationResponse = data;
        requestId = _requestId;
      }
      finishTransaction(isNewApiForShareUrlEnabled ? 'install-from-share-url' : 'bulk-install', {
        ...transactionPayload,
        requestId
      });

      // Ignoring errors so we will have parity with the old installer
      if (!isEmpty(installationResponse.installErrors)) {
        panoramaLogger(JSON.stringify(installationResponse.installErrors), {
          requestId
        });
      }
      const installedApps = (_installationResponse = installationResponse) == null ? void 0 : _installationResponse.installedApps;
      if (isEmpty(installedApps)) {
        reportInstallFailed(requestId);
        await handleError({
          error: new Error('Empty installed apps'),
          requestId,
          ...INSTALLER_ERRORS.INSTALLATION_FAILED
        });
        return;
      } else {
        const provisionSuccessData = convertResponseToProvisionSuccess({
          tenantId,
          mainInstalledAppId: appId,
          installedApps: installedApps
        });
        if (provisionSuccessData) {
          var _provisionSuccessData;
          onProvisionSuccess == null || onProvisionSuccess(provisionSuccessData);
          reportInstallSuccess({
            requestId,
            instanceId: ((_provisionSuccessData = provisionSuccessData.installedApps[0]) == null ? void 0 : _provisionSuccessData.instanceId) || ''
          });
        }
        setIsFinishLoading(true);
        await wait(500); // Sometime installation is very quick, and we don't want to show the loader for a very short time

        return provisionSuccessData;
      }
    } catch (error) {
      await handleError({
        error,
        ...INSTALLER_ERRORS.INSTALLATION_FAILED
      });
      return;
    }
  };
};
//# sourceMappingURL=useProvisionApp.js.map